import { useEffect, useMemo, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { ContentfulRichText } from '@wbk/contentful';
import type { ContentfulRichText as ContentfulRichTextType, WebSection } from '@wbk/contentful/api';
// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Type definitions
interface ImageData {
  url?: string;
}

interface AnimatedImageItem {
  image?: ImageData;
  title?: string;
  subtitle?: string;
  desc?: string;
  description?: string | ContentfulRichTextType | undefined;
}

type WebSectionItems = WebSection & {
  contentCollection?: {
    items?: AnimatedImageItem[];
  };
};

interface ImageComponentProps {
  imageUrl?: string;
  className?: string;
}

interface StatsFrameProps {
  title: string;
  desc: string | ContentfulRichTextType | undefined;
  colorClass?: string;
  subtitle?: string;
}

interface RiyadhSeason2025Props {
  section?: WebSectionItems;
}

const ImageComponent: React.FC<ImageComponentProps> = ({ imageUrl, className = '' }) => {
  return (
    <div className={`absolute inset-0 flex items-center justify-center ${className}`}>
      <div className='relative w-[85vw] sm:w-[60vw]'>
        <img
          className='mt-[20px] block size-full max-w-none object-contain sm:mt-[60px]'
          src={imageUrl || '/api/placeholder/666/419'}
          alt='Riyadh Season'
        />
      </div>
    </div>
  );
};

const StatsFrame: React.FC<StatsFrameProps> = ({
  title,
  desc,
  colorClass = 'text-[#00d2b0]',
  subtitle,
}) => {
  const renderDesc = desc ? (
    typeof desc === 'string' ? (
      <p>{desc}</p>
    ) : (
      <ContentfulRichText description={desc} />
    )
  ) : null;
  return (
    <div className='stats-content flex h-full flex-col items-center justify-center gap-2 sm:gap-4'>
      <div className='text-center font-semibold'>
        <span className={`text-[20px] leading-none sm:text-[40px] ${colorClass}`}>{title}</span>
        <span className='ml-2 text-[13px] font-normal text-white'>{subtitle}</span>
      </div>
      <div className='max-w-[340px] text-center text-[13px] text-[rgba(255,255,255,0.8)] sm:max-w-[305px]'>
        {renderDesc}
      </div>
    </div>
  );
};

const RiyadhSeason2025 = ({ section }: RiyadhSeason2025Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const textRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  const items: AnimatedImageItem[] = useMemo(
    () => section?.contentCollection?.items || [],
    [section?.contentCollection?.items]
  );

  useEffect(() => {
    if (!containerRef.current || !headerRef.current) return;

    const ctx = gsap.context(() => {
      // Header animation on load
      const headerElements = headerRef.current?.querySelectorAll('.header-element');
      if (headerElements) {
        gsap.from(headerElements, {
          y: 50,
          opacity: 0,
          duration: 1,
          stagger: 0.2,
          ease: 'power3.out',
        });
      }

      // Create the main timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
          pin: '.pin-container',
          anticipatePin: 1,
        },
      });

      // Set initial states for images and text
      imageRefs.current.forEach((img, i) => {
        if (!img) return;
        gsap.set(img, {
          rotate: i === 0 ? 0 : 180,
          zIndex: i === 0 ? 10 : 1,
        });
      });

      textRefs.current.forEach((text, i) => {
        if (!text) return;
        gsap.set(text, { y: i === 0 ? 0 : 100, opacity: i === 0 ? 1 : 0 });
      });

      // Create animations dynamically based on the number of items
      items.forEach((_, i) => {
        if (i === items.length - 1) return; // Skip the last item for transitions

        const currentImg = imageRefs.current[i];
        const nextImg = imageRefs.current[i + 1];
        const currentText = textRefs.current[i];
        const nextText = textRefs.current[i + 1];

        tl.to(
          currentImg,
          {
            rotation: -180,
            zIndex: 1,
            duration: 1,
            ease: 'power2.inOut',
          },
          i
        );

        tl.to(
          nextImg,
          {
            rotation: 0,
            zIndex: 10,
            duration: 1,
            ease: 'power2.inOut',
          },
          i
        );

        tl.to(
          [containerRef.current, '#top-bg-div'],
          {
            backgroundColor: nextImg?.getAttribute('data-bg-color') || '#000',
            duration: 1,
            ease: 'power2.inOut',
          },
          i
        );

        if (currentText) {
          tl.to(
            currentText,
            {
              y: -100,
              opacity: 0,
              duration: 0.5,
              ease: 'power2.in',
            },
            i
          );
        }

        if (nextText) {
          tl.to(
            nextText,
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              ease: 'power2.out',
            },
            i + 0.3
          );
        }
      });

      // Parallax effect for the entire content
      gsap.to('.content-wrapper', {
        y: -100,
        ease: 'none',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, [items]);

  return (
    <div
      ref={containerRef}
      className='relative !m-0 mx-auto overflow-hidden'
      style={{
        transition: 'background 0.5s ease',
        background: '#3885FF',
      }}
    >
      <div className='pin-container h-[120vh] w-full'>
        <div className='content-wrapper relative size-full'>
          <div className='relative z-20 flex min-h-screen w-full flex-col items-center justify-center gap-12 pt-20'>
            <div
              id='top-bg-div'
              style={{
                width: '100%',

                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 20,
                background: '#3885FF',
                transition: 'background 0.5s ease',
              }}
              className='h-[74%] sm:h-[50%]'
            ></div>

            <div className='mt-0 flex w-full flex-col items-center justify-center sm:mt-[-20vh]'>
              <div
                ref={headerRef}
                className='relative z-20 flex w-[85vw] flex-col items-center justify-center gap-6 pb-32 pt-0 sm:w-[60vw] sm:py-10 lg:flex-row lg:gap-12'
              >
                <div className='header-element text-center font-bold lg:text-left'>
                  <h1 className='rtl:font-expo-arabic text-[48px] font-bold uppercase leading-[1em] text-white lg:text-[64px] lg:normal-case rtl:text-right'>
                    <span>
                      {(section?.title || 'Riyadh Season').split(' ').map((word) => (
                        <div key={word} className='block'>
                          {word}
                          <br />
                        </div>
                      ))}
                    </span>
                  </h1>
                </div>

                <div className='header-element flex justify-center lg:h-[69px] lg:w-[0px] lg:items-center'>
                  <div className='flex-none lg:rotate-90'>
                    <img src='/images/illustration/divider.svg' alt='divider' />
                  </div>
                </div>

                <div className='flex flex-col gap-4 text-center lg:text-left'>
                  <div className='header-element text-center text-[17px] font-semibold uppercase text-white lg:text-start'>
                    {section?.subtitle}
                  </div>
                  <div className='header-element max-w-[507px] font-normal text-[rgba(255,255,255,0.5)]'>
                    <div className='text-center text-[14px] lg:text-start lg:text-[16px]'>
                      {section?.collapsableDescription && (
                        <ContentfulRichText description={section?.collapsableDescription} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Frame */}
              <div className='mt-[-20px] w-[80vw] max-w-[340px] sm:max-w-[820px]'>
                {/* Images Container */}
                <div className='relative h-full w-full'>
                  {items.map((item, i) => (
                    <div
                      key={(item.title ?? '') + i}
                      ref={(el) => {
                        imageRefs.current[i] = el;
                      }}
                      className='image-container absolute inset-0'
                      style={{ zIndex: items.length - i }}
                      data-bg-color={i === 0 ? '#3885FF' : i === 1 ? '#987BFF' : '#00D2B0'}
                    >
                      <ImageComponent imageUrl={item.image?.url} />
                    </div>
                  ))}
                </div>

                {/* Text Content */}
                <div className='relative z-[99] flex justify-center'>
                  <div className='relative top-[-117px] h-[160px] w-[441px] overflow-visible sm:top-[20px]'>
                    {/* <div className='relative flex justify-center'>
                  <div className='relative top-[20px] h-[120px] w-[441px]'> */}
                    <div
                      ref={(el) => {
                        textRefs.current[0] = el;
                      }}
                      className='absolute inset-0'
                    >
                      <StatsFrame
                        title={items[0]?.title || ''}
                        desc={items[0]?.description || ''}
                        subtitle={items[0]?.subtitle}
                      />
                    </div>

                    <div
                      ref={(el) => {
                        textRefs.current[1] = el;
                      }}
                      className='absolute inset-0'
                    >
                      <StatsFrame
                        title={items[1]?.title || ''}
                        desc={items[1]?.description || ''}
                        colorClass='text-[#ff9b27]'
                        subtitle={items[1]?.subtitle}
                      />
                    </div>

                    <div
                      ref={(el) => {
                        textRefs.current[2] = el;
                      }}
                      className='absolute inset-0'
                    >
                      <StatsFrame
                        title={items[2]?.title || ''}
                        desc={items[2]?.description || ''}
                        colorClass='text-[#987bff]'
                        subtitle={items[2]?.subtitle}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiyadhSeason2025;
